'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter, usePathname } from 'next/navigation';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  company_id: string | null;
  onboarding_done: boolean;
}

export interface Company {
  id: string;
  name: string;
  slug: string | null;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  subscription_status: string | null;
  plan: string | null;
  // NULL em clientes antigos: acesso legado permanece inalterado.
  pro_expires_at: string | null;
}

interface AuthContextValue {
  user: any | null;
  session: any | null;
  profile: UserProfile | null;
  company: Company | null;
  loading: boolean;
  tenantId: string | null;
  trialExpired: boolean;
  daysLeftInTrial: number;
  signUp: (email: string, password: string, fullName?: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  markOnboardingDone: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// Routes that must never trigger auto-redirect to /login
const PUBLIC_PATHS = [
  '/login',
  '/cadastro',
  '/planos',
  '/acesso',
  '/esqueci-senha',
  '/orcamento',
  '/auth/callback',
  '/auth/confirm',
  '/oauth/consent',
  '/redefinir-senha',
];

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  const supabase = createClient();
  const router = useRouter();
  const pathname = usePathname();

  const tenantId = profile?.company_id ?? null;

  const trialEndsAt = company?.trial_ends_at
    ? new Date(company.trial_ends_at)
    : null;

  const trialExpired =
    company?.subscription_status === 'trialing' &&
    !!trialEndsAt &&
    trialEndsAt.getTime() < Date.now();

  const annualExpired = !!company?.pro_expires_at &&
    new Date(company.pro_expires_at).getTime() <= Date.now();
  const accessExpired = trialExpired || annualExpired ||
    company?.subscription_status === 'expired';

  const daysLeftInTrial =
    company?.subscription_status === 'trialing' && trialEndsAt
      ? Math.max(
          0,
          Math.ceil(
            (trialEndsAt.getTime() - Date.now()) /
              (1000 * 60 * 60 * 24)
          )
        )
      : 0;

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data: prof, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('fetchProfile error:', error.message);
        return null;
      }

      if (prof) {
        setProfile(prof as UserProfile);

        if (prof.company_id) {
          const { data: comp } = await supabase
            .from('companies')
            .select('*')
            .eq('id', prof.company_id)
            .maybeSingle();

          if (comp) {
            // Confere compras anuais usando e-mail CONFIRMADO no Auth,
            // nunca o campo editavel user_profiles.email.
            // Tambem revalida licencas anuais existentes (vencimento/estorno).
            const isAnnual = !!comp.pro_expires_at;
            const needsClaim = isAnnual ||
              comp.subscription_status !== 'active' || comp.plan !== 'pro';
            if (needsClaim) {
              const { data: annualGranted, error: annualError } =
                await supabase.rpc('claim_fluxosim_annual_entitlement');
              if (annualError) {
                // Migração ainda não instalada: não interrompe o app legado.
                console.warn('Annual entitlement check:', annualError.message);
              }
              if (!annualGranted && !isAnnual &&
                  (comp.subscription_status !== 'active' || comp.plan !== 'pro')) {
                await supabase.rpc('claim_eduzz_entitlement');
              }
              const { data: refreshedComp } = await supabase
                .from('companies')
                .select('*')
                .eq('id', prof.company_id)
                .maybeSingle();
              setCompany((refreshedComp ?? comp) as Company);
            } else {
              setCompany(comp as Company);
            }
          }
        }

        return prof as UserProfile;
      }

      return null;
    } catch (e) {
      console.error('fetchProfile exception:', e);
      return null;
    }
  }, [supabase]);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    await fetchProfile(user.id);
  }, [user, fetchProfile]);

  useEffect(() => {
    let mounted = true;

    const applySession = (s: any | null) => {
      if (!mounted) return;

      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);

      if (s?.user) {
        void fetchProfile(s.user.id);
      } else {
        setProfile(null);
        setCompany(null);
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      applySession(s);
    });

    supabase.auth
      .getSession()
      .then(({ data: { session: s }, error }) => {
        if (!mounted) return;

        if (error) {
          console.error('getSession error:', error.message);
          setLoading(false);
          return;
        }

        applySession(s);
      })
      .catch((e) => {
        console.error('getSession exception:', e);
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && pathname === '/') {
      const params = new URLSearchParams(window.location.search);

      if (params.has('code')) {
        if (!params.has('type')) {
          params.set('type', 'recovery');
        }

        router.replace(`/auth/callback?${params.toString()}`);
        return;
      }
    }

    if (loading) return;

    const isPublic = PUBLIC_PATHS.some((p) => pathname?.startsWith(p));

    if (!user && !isPublic) {
      router.replace('/login');
      return;
    }

    const accessExempt =
      isPublic ||
      pathname?.startsWith('/assinar') ||
      pathname?.startsWith('/planos');

    if (user && company && accessExpired && !accessExempt) {
      router.replace('/assinar');
      return;
    }

    if (
      user &&
      company &&
      company.subscription_status === 'active' &&
      company.plan === 'pro' && !annualExpired &&
      pathname?.startsWith('/assinar')
    ) {
      router.replace('/');
    }
  }, [user, company, accessExpired, annualExpired, loading, pathname, router]);

  const signUp = async (
    email: string,
    password: string,
    fullName = ''
  ) => {
    const siteUrl =
      typeof window !== 'undefined'
        ? window.location.origin
        : (
            process.env.NEXT_PUBLIC_SITE_URL ||
            'https://fluxosim-app.frontusinagem.workers.dev'
          );

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${siteUrl}/auth/confirm`,
      },
    });

    if (error) throw error;

    if (data.user) {
      await createTenantForUser(
        data.user.id,
        fullName,
        email
      );
    }

    return data;
  };

  const createTenantForUser = async (
    userId: string,
    fullName: string,
    email: string
  ) => {
    try {
      const { data: companyId, error: rpcErr } =
        await supabase.rpc('create_tenant_for_user', {
          p_user_id: userId,
          p_full_name: fullName || '',
          p_email: email,
        });

      if (rpcErr || !companyId) {
        console.error(
          'createTenant RPC error:',
          rpcErr?.message
        );
        return;
      }

      const { data: comp } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .maybeSingle();

      if (comp) {
        setCompany(comp as Company);
      }
    } catch (e) {
      console.error(
        'createTenantForUser exception:',
        e
      );
    }
  };

  const signIn = async (
    email: string,
    password: string
  ) => {
    const { data, error } =
      await supabase.auth.signInWithPassword({
        email,
        password,
      });

    if (error) throw error;

    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setCompany(null);
    router.replace('/login');
  };

  const resetPassword = async (email: string) => {
    const siteUrl =
      typeof window !== 'undefined'
        ? window.location.origin
        : (process.env.NEXT_PUBLIC_SITE_URL || 'https://fluxosim-app.frontusinagem.workers.dev');

    const { error } =
      await supabase.auth.resetPasswordForEmail(
        email,
        {
          redirectTo:
            `${siteUrl}/auth/confirm?type=recovery`,
        }
      );

    if (error) throw error;
  };

  const markOnboardingDone = async () => {
    if (!user) return;

    await supabase
      .from('user_profiles')
      .update({ onboarding_done: true })
      .eq('id', user.id);

    setProfile((prev) =>
      prev
        ? { ...prev, onboarding_done: true }
        : prev
    );
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        company,
        loading,
        tenantId,
        trialExpired,
        daysLeftInTrial,
        signUp,
        signIn,
        signOut,
        resetPassword,
        refreshProfile,
        markOnboardingDone,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
